import React, { type FC, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getWeblinkFromPublicId } from 'reactApp/modules/file/utils';
import { getPublicLimits } from 'reactApp/modules/public/public.selectors';
import { getCurrentRouteId } from 'reactApp/modules/router/router.selectors';
import { EViewMode } from 'reactApp/modules/settings/settings.types';
import type { RootState } from 'reactApp/store';
import { Checkbox } from 'reactApp/ui/DataListItemCommonComponents/Checkbox/Checkbox';
import { Download } from 'reactApp/ui/DataListItemCommonComponents/Download/Download';
import { Date } from 'reactApp/ui/DataListItemRow/components/Date/Date';
import { ItemName } from 'reactApp/ui/DataListItemRow/components/ItemName/ItemName';
import { ItemTip } from 'reactApp/ui/DataListItemRow/components/ItemTip/ItemTip';
import { Size } from 'reactApp/ui/DataListItemRow/components/Size/Size';
import { TableColumn } from 'reactApp/ui/DataListItemRow/components/TableColumn/TableColumn';
import type { PublicItemProps } from 'reactApp/ui/DataListItemRow/DataListItemRow.types';
import { FilePic } from 'reactApp/ui/FilePic/FilePic';
import { useCompactView } from 'reactApp/ui/VirtualList/VirtualList.hooks';

import styles from '../../DataListItemRow.new.css';

export const PublicItem: FC<PublicItemProps> = (props) => {
    const {
        id,
        tipData,
        isFolder,
        isPublic,
        isSelected,
        thumbUrl,
        isVirus,
        kind,
        author,
        name,
        size,
        mtime,
        ext,
        storage,
        onDownload,
        onCheckboxClick,
        parentItemRef,
        showDownloadButton,
    } = props;

    const isCompact = useCompactView();

    const tipTargetRef = useRef(null);

    const routeId = useSelector(getCurrentRouteId);
    const weblinkUrl = getWeblinkFromPublicId(routeId);
    const downloadLimits = useSelector((state: RootState) => getPublicLimits(state, weblinkUrl || ''));
    const downloadLeft = downloadLimits && downloadLimits.left;

    return (
        <>
            <ItemTip target={parentItemRef} tipData={tipData} isFolder={isFolder ?? false} />
            <TableColumn>
                <Checkbox viewType={EViewMode.list} className={styles.check} onCheckboxClick={onCheckboxClick} isSelected={isSelected} />
                <FilePic
                    ref={tipTargetRef}
                    isFolder={isFolder}
                    thumbUrl={thumbUrl}
                    kind={kind}
                    isVirus={isVirus}
                    ext={ext || ''}
                    author={author}
                    isPublic={isPublic}
                />
            </TableColumn>

            {isCompact ? (
                <TableColumn width="100%" overflow="hidden">
                    <div className={styles.nameWrapper}>
                        <ItemName name={name} ext={ext || ''} id={id} />
                        <span className={styles.responsiveDateWrapper}>
                            <Size size={size} isInline />
                            {!!mtime && <>&nbsp;•&nbsp;</>}
                            <Date date={mtime} className={styles.date} />
                        </span>
                    </div>
                </TableColumn>
            ) : (
                <>
                    <TableColumn width="100%" overflow="hidden">
                        <ItemName name={name} ext={ext || ''} id={id} />
                    </TableColumn>
                    <TableColumn flexBasis={132} flexShrink={0} justifyContent="flex-end">
                        <Date date={mtime} />
                    </TableColumn>
                    <TableColumn flexBasis={132} flexShrink={0} justifyContent="center">
                        <Size size={size} />
                    </TableColumn>
                </>
            )}

            <TableColumn flexBasis={100} flexShrink={0} justifyContent="flex-end" gap={20}>
                {showDownloadButton && (
                    <Download
                        isAchivedLimit={downloadLeft === 0}
                        onClick={onDownload}
                        ext={isFolder ? 'zip' : ext}
                        size={size}
                        id={id}
                        storage={storage}
                        viewType={EViewMode.list}
                    />
                )}
            </TableColumn>
        </>
    );
};
